import { useState, useMemo, useCallback, useEffect } from "react";
import {
  getValidToHours,
  getValidToMinutes,
  getValidToPeriods,
  getDaysStatusText,
  getTimeStatusText,
  getNextValidTime,
  isValidTimeRange,
  DEFAULT_DAYS,
  DEFAULT_FROM_TIME,
  DEFAULT_TO_TIME,
} from "../utility/scheduleTimeUtils";

const ALL_HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
const ALL_MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));
const ALL_PERIODS = ["AM", "PM"];

export const useScheduleTimeFrame = ({ onScheduleChange, initialValues }) => {
  const [daysEnabled, setDaysEnabled] = useState(initialValues?.daysEnabled ?? false);
  const [timeEnabled, setTimeEnabled] = useState(initialValues?.timeEnabled ?? false);
  const [selectedDays, setSelectedDays] = useState(initialValues?.days ?? [...DEFAULT_DAYS]);
  const [fromTime, setFromTime] = useState(initialValues?.fromTime ?? { ...DEFAULT_FROM_TIME });
  const [toTime, setToTime] = useState(initialValues?.toTime ?? { ...DEFAULT_TO_TIME });

  useEffect(() => {
    if (!initialValues) return;
    if (initialValues.daysEnabled !== undefined) setDaysEnabled(initialValues.daysEnabled);
    if (initialValues.timeEnabled !== undefined) setTimeEnabled(initialValues.timeEnabled);
    if (initialValues.days) setSelectedDays(initialValues.days);
    if (initialValues.fromTime) setFromTime(initialValues.fromTime);
    if (initialValues.toTime) setToTime(initialValues.toTime);
  }, [initialValues]);

  const notifyChange = useCallback(
    (updates) => {
      if (onScheduleChange) {
        onScheduleChange({
          daysEnabled,
          timeEnabled,
          days: selectedDays,
          fromTime,
          toTime,
          ...updates,
        });
      }
    },
    [onScheduleChange, daysEnabled, timeEnabled, selectedDays, fromTime, toTime]
  );

  const validToHours = useMemo(
    () => getValidToHours(fromTime, toTime.period, ALL_HOURS),
    [fromTime, toTime.period]
  );

  const validToMinutes = useMemo(
    () => getValidToMinutes(fromTime, toTime.hour, toTime.period, ALL_MINUTES),
    [fromTime, toTime.hour, toTime.period]
  );

  const validToPeriods = useMemo(
    () => getValidToPeriods(fromTime.period, ALL_PERIODS),
    [fromTime.period]
  );

  const daysStatusText = useMemo(
    () => (daysEnabled ? getDaysStatusText(selectedDays) : "Every day"),
    [daysEnabled, selectedDays]
  );

  const timeStatusText = useMemo(
    () => (timeEnabled ? getTimeStatusText(fromTime, toTime) : null),
    [timeEnabled, fromTime, toTime]
  );

  return {
    daysEnabled,
    timeEnabled,
    selectedDays,
    fromTime,
    toTime,
    hours: ALL_HOURS,
    minutes: ALL_MINUTES,
    periods: ALL_PERIODS,
    validToHours,
    validToMinutes,
    validToPeriods,
    daysStatusText,
    timeStatusText,
    setDaysEnabled,
    setTimeEnabled,
    setSelectedDays,
    setFromTime,
    setToTime,
    notifyChange,
  };
};

export const useTimeChangeHandlers = ({
  fromTime,
  toTime,
  setFromTime,
  setToTime,
  notifyChange,
}) => {
  const handleFromTimeChange = useCallback(
    (field, value) => {
      const updatedFrom = { ...fromTime, [field]: value };
      let adjustedTo = toTime;

      if (field === "period" && value === "PM" && toTime.period === "AM") {
        adjustedTo = { ...toTime, period: "PM" };
      }

      if (!isValidTimeRange(updatedFrom, adjustedTo)) {
        adjustedTo = getNextValidTime(updatedFrom);
      } else if (
        updatedFrom.hour === adjustedTo.hour &&
        updatedFrom.period === adjustedTo.period &&
        parseInt(adjustedTo.minute) <= parseInt(updatedFrom.minute)
      ) {
        adjustedTo = getNextValidTime(updatedFrom);
      }

      setFromTime(updatedFrom);
      if (adjustedTo !== toTime) setToTime(adjustedTo);
      notifyChange({ fromTime: updatedFrom, toTime: adjustedTo });
    },
    [fromTime, toTime, setFromTime, setToTime, notifyChange]
  );

  const handleToTimeChange = useCallback(
    (field, value) => {
      const updatedTo = { ...toTime, [field]: value };
      setToTime(updatedTo);
      notifyChange({ toTime: updatedTo });
    },
    [toTime, setToTime, notifyChange]
  );

  return { handleFromTimeChange, handleToTimeChange };
};

export const useDaySelectionHandlers = ({ selectedDays, setSelectedDays, notifyChange }) => {
  const handleDayToggle = useCallback(
    (dayValue) => {
      const next = selectedDays.includes(dayValue)
        ? selectedDays.filter((d) => d !== dayValue)
        : [...selectedDays, dayValue];
      setSelectedDays(next);
      notifyChange({ days: next });
    },
    [selectedDays, setSelectedDays, notifyChange]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedDays([...DEFAULT_DAYS]);
    notifyChange({ days: [...DEFAULT_DAYS] });
  }, [setSelectedDays, notifyChange]);

  const handleClearAll = useCallback(() => {
    setSelectedDays([]);
    notifyChange({ days: [] });
  }, [setSelectedDays, notifyChange]);

  return { handleDayToggle, handleSelectAll, handleClearAll };
};

export const useToggleHandlers = ({
  setDaysEnabled,
  setTimeEnabled,
  notifyChange,
}) => {
  const handleDaysToggle = useCallback(
    (enabled) => {
      setDaysEnabled(enabled);
      if (!enabled) {
        setTimeEnabled(false);
        notifyChange({ daysEnabled: false, timeEnabled: false });
      } else {
        notifyChange({ daysEnabled: true });
      }
    },
    [setDaysEnabled, setTimeEnabled, notifyChange]
  );

  const handleTimeToggle = useCallback(
    (enabled) => {
      setTimeEnabled(enabled);
      notifyChange({ timeEnabled: enabled });
    },
    [setTimeEnabled, notifyChange]
  );

  return { handleDaysToggle, handleTimeToggle };
};
